import { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '@/components/common/Loader';
import { Sheet } from '@/components/common/Sheet';
import { UserAvatar } from '@/components/social/UserAvatar';
import { useHaptics } from '@/hooks/useHaptics';
import { dataDelBlocco, nomeBloccato } from '@/lib/qsocials/bloccati';
import { useListBlockedQuery, useUnblockUserMutation } from '@/services/socialApi';
import type { BlockedFriendshipDTO } from '@/types/social';

/**
 * «Account bloccati» (punto 7 del ridisegno Amici, L-2418 (a) — PR-3).
 *
 * Fonte: `GET /social/friends/blocked`, che contiene SOLO i blocchi messi da me (K4): quelli
 * subiti non arrivano mai, quindi la pagina non ha modo di mostrarli. Sblocca: `DELETE
 * /social/friends/block/:id` (`useUnblockUserMutation`, gia' nel client), dietro una conferma; il
 * tag `Blocked` che invalida rilegge l'elenco e la riga sparisce perche' il server non la manda
 * piu', non perche' la si toglie a mano.
 */
export function FriendsBlocked() {
  const { t, i18n } = useTranslation('general');
  const haptics = useHaptics();
  const elenco = useListBlockedQuery();
  const [sblocca, sbloccoStato] = useUnblockUserMutation();
  const [daSbloccare, setDaSbloccare] = useState<BlockedFriendshipDTO | null>(null);
  const [erroreSblocco, setErroreSblocco] = useState(false);

  // Chi ha il foglio aperto ADESSO: la risposta di uno sblocco arriva dopo, e il foglio nel frattempo
  // puo' essere stato chiuso o riaperto su un'altra riga. Un errore (o una chiusura) vale solo se il
  // foglio e' ancora sulla riga a cui la richiesta si riferiva (revisione indipendente #504, 🟡1).
  const aperto = useRef<string | null>(null);
  useEffect(() => {
    aperto.current = daSbloccare?.friendUserId ?? null;
  }, [daSbloccare]);

  // `aperto` si scrive ANCHE qui, negli handler, e non solo nell'effetto: il tasto indietro di Telegram
  // chiude il foglio da fuori da un evento DOM, l'effetto passivo gira dopo, e un fallimento che arriva in
  // quella finestra troverebbe la ref ancora sulla vecchia riga (revisione indipendente #504, 2° giro, 🟢2).
  // Sono handler e non il corpo del render: non e' il pattern che `useLivelloIndietro` vieta.
  const chiudi = () => {
    aperto.current = null;
    setDaSbloccare(null);
    setErroreSblocco(false);
  };

  // Un'apertura non e' una chiusura: «Sblocca» di un'altra riga si puo' premere a foglio aperto (il foglio
  // non intrappola il focus), quindi il reset dell'errore sta qui e non solo in `chiudi()` (🟡1).
  const apri = (b: BlockedFriendshipDTO) => {
    aperto.current = b.friendUserId;
    // Ogni apertura parte pulita: l'errore di un tentativo precedente non e' di questo foglio.
    setErroreSblocco(false);
    setDaSbloccare(b);
  };

  const conferma = async () => {
    if (!daSbloccare) return;
    const id = daSbloccare.friendUserId;
    haptics.impact('medium');
    try {
      await sblocca({ friendUserId: id }).unwrap();
      if (aperto.current === id) chiudi();
    } catch {
      if (aperto.current === id) setErroreSblocco(true);
    }
  };

  const nomeDaSbloccare = daSbloccare ? nomeBloccato(daSbloccare) : '';
  const righe = elenco.data;

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <Link
        to="/friends"
        data-testid="bloccati-indietro"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-brand"
      >
        <ChevronLeft aria-hidden className="h-4 w-4" />
        {t('social.area_friends', { defaultValue: 'Friends' })}
      </Link>

      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche la frase sotto (decisione di tma-orcb, 2026-10-03). */}
      <h1 className="sr-only">
        {t('social.blocked_title', { defaultValue: 'Blocked accounts' })}
      </h1>
      <p data-testid="bloccati-frase" className="sr-only">
        {t('social.blocked_intro', {
          defaultValue:
            "They can't message you, send you a friend request or see when you're online. If you unblock someone, they don't come back to your friends.",
        })}
      </p>

      {righe === undefined && elenco.isError ? (
        <div role="alert" className="flex flex-col items-start gap-2 rounded-2xl bg-bg-secondary p-4">
          <p className="text-sm text-fg">
            {t('social.load_error', { defaultValue: "Couldn't load the list." })}
          </p>
          <button
            type="button"
            data-testid="bloccati-riprova"
            onClick={() => void elenco.refetch()}
            className="h-10 rounded-xl bg-bg-tertiary px-4 text-sm font-bold text-fg"
          >
            {t('social.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      ) : righe === undefined ? (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      ) : righe.length === 0 ? (
        <p
          data-testid="bloccati-vuoto"
          className="rounded-2xl bg-bg-secondary p-6 text-center text-sm text-fg-muted"
        >
          {t('social.blocked_empty', { defaultValue: "You haven't blocked anyone." })}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-2xl bg-bg-secondary/60" data-testid="bloccati-elenco">
          {righe.map((b) => {
            const nome = nomeBloccato(b);
            const data = dataDelBlocco(b.blockedAt, i18n?.language);
            return (
              <li
                key={b.friendUserId}
                data-testid="riga-bloccato"
                className="flex min-h-[64px] items-center gap-3 px-4 py-2"
              >
                <UserAvatar userId={b.friendUserId} label={nome} avatarUrl={b.avatarUrl} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-fg">{nome}</p>
                  {data ? (
                    <p className="text-xs text-fg-muted">
                      {t('social.blocked_since', { date: data, defaultValue: 'Blocked on {{date}}' })}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  data-testid={`sblocca-${b.friendUserId}`}
                  aria-label={t('social.unblock_aria', { name: nome, defaultValue: 'Unblock {{name}}' })}
                  onClick={() => {
                    haptics.impact('light');
                    apri(b);
                  }}
                  className="h-9 shrink-0 rounded-xl bg-bg-tertiary px-4 text-sm font-bold text-fg"
                >
                  {t('social.unblock', { defaultValue: 'Unblock' })}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Sheet
        open={daSbloccare !== null}
        onClose={chiudi}
        label={t('social.unblock_title', { name: nomeDaSbloccare, defaultValue: 'Unblock {{name}}?' })}
      >
        <div className="flex flex-col gap-3 px-5 pb-6 pt-2">
          <p className="text-lg font-extrabold text-fg">
            {t('social.unblock_title', { name: nomeDaSbloccare, defaultValue: 'Unblock {{name}}?' })}
          </p>
          <p className="text-sm text-fg">
            {t('social.unblock_text', {
              defaultValue:
                "They'll be able to send you a friend request and follow you again. They only come back to your friends if you accept one.",
            })}
          </p>
          {erroreSblocco ? (
            <p role="alert" className="text-sm text-danger">
              {t('social.unblock_error', { defaultValue: "Couldn't unblock. Try again." })}
            </p>
          ) : null}
          <button
            type="button"
            data-testid="sblocca-conferma"
            disabled={sbloccoStato.isLoading}
            onClick={() => void conferma()}
            className="mt-2 h-12 rounded-2xl bg-brand font-extrabold text-brand-fg disabled:opacity-50"
          >
            {t('social.unblock', { defaultValue: 'Unblock' })}
          </button>
          <button
            type="button"
            data-testid="sblocca-annulla"
            onClick={chiudi}
            className="h-12 rounded-2xl bg-bg-secondary font-bold text-fg"
          >
            {t('social.unblock_cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      </Sheet>
    </div>
  );
}
